import { CSSProperties, FC, useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AllieSelectOptionProps } from './Select.types';
import { IonList, IonPopover, IonCheckbox } from '@ionic/react';
import {
  BadgeMultipleSelect,
  IconContainer,
  ParentContainer,
  StyledDropdownSearchBar,
  StyledItem,
  StyledSearchableItemDropdown,
  StyledTextContainer,
} from './SearchableSelect.styles';
import { caretDownOutline, caretUpOutline } from 'ionicons/icons';
import { IonPopoverProps } from '../../common/components.types';
import { useVirtualizer } from '@tanstack/react-virtual';

export type handleOnSelect = (value: string | number | (string | number)[]) => void;

interface AllieSearchableSelect {
  handleOnSelect: handleOnSelect;
  options: AllieSelectOptionProps[];
  selectedValue: string | string[] | undefined;
  disabled?: boolean;
  trackSelectionEvent?: (field: string, isInputSearch: boolean) => void;
  field?: string;
  style?: CSSProperties;
  className?: string;
  placeholder?: string;
  multiple?: boolean;
  selectAllLabel?: string;
  showSelectAllIndeterminate?: boolean;
  popoverProps?: IonPopoverProps;
  searchPlaceholder?: string;
  virtualized?: boolean;
  itemHeight?: number;
  displayTextSize?: string;
  displayTextWeight?: number;
  multiSelectLabel?: string;
  showBadgeCount?: boolean;
}

const MAX_HEIGHT = 400;
const VIRTUALIZATION_THRESHOLD = 25;

export const AllieSearchableSelect: FC<AllieSearchableSelect> = ({
  handleOnSelect,
  options,
  disabled,
  selectedValue,
  trackSelectionEvent,
  field = '',
  style,
  className,
  placeholder = '',
  multiple = false,
  selectAllLabel,
  showSelectAllIndeterminate = true,
  popoverProps,
  searchPlaceholder,
  virtualized = false,
  itemHeight = 54,
  displayTextSize = '12px',
  displayTextWeight,
  multiSelectLabel,
  showBadgeCount = true,
}) => {
  // Estados
  const [searchText, setSearchText] = useState('');
  const [selected, setSelected] = useState<string | string[] | undefined>(selectedValue);
  const [showPopover, setShowPopover] = useState(false);

  const parentRef = useRef<HTMLDivElement>(null);
  const popoverId = useMemo(() => uuidv4(), []);

  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    return options.filter((option) => option.text.toLowerCase().includes(searchText.toLowerCase()));
  }, [options, searchText]);

  const shouldVirtualize = virtualized && filteredOptions.length > VIRTUALIZATION_THRESHOLD;

  const getVirtualItemKey = useCallback(
    (index: number) => filteredOptions[index]?.value ?? index,
    [filteredOptions]
  );

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: 5,
    enabled: shouldVirtualize && showPopover,
    getItemKey: getVirtualItemKey,
    initialRect: { width: 0, height: MAX_HEIGHT },
  });

  const handleOptionSelect = useCallback(
    (option: AllieSelectOptionProps) => {
      if (option.disabled) return;
      if (multiple) {
        const newSelected = Array.isArray(selected) ? [...selected] : [];
        const index = newSelected.indexOf(option.text);

        if (index > -1) {
          newSelected.splice(index, 1);
        } else {
          newSelected.push(option.text);
        }

        setSelected(newSelected);
        handleOnSelect(
          newSelected.map(
            (text) => options.find((opt) => opt.text === text)?.value as string | number
          )
        );
      } else {
        setSelected(option.text);
        handleOnSelect(option.value);
        setShowPopover(false);
      }
    },
    [handleOnSelect, multiple, options, selected]
  );

  const { enabledOptions, disabledTextSet, textToValue } = useMemo(() => {
    const enabled: AllieSelectOptionProps[] = [];
    const disabledTexts = new Set<string>();
    const lookup = new Map<string, string | number>();

    for (const option of options) {
      lookup.set(option.text, option.value);
      if (option.disabled) {
        disabledTexts.add(option.text);
      } else {
        enabled.push(option);
      }
    }

    return { enabledOptions: enabled, disabledTextSet: disabledTexts, textToValue: lookup };
  }, [options]);

  const enabledTextSet = useMemo(
    () => new Set(enabledOptions.map((o) => o.text)),
    [enabledOptions]
  );

  const handleSelectAll = useCallback(() => {
    const selectedArr = Array.isArray(selected) ? selected : [];
    const disabledKept = selectedArr.filter((t) => disabledTextSet.has(t));
    const enabledSelectedCount = selectedArr.filter((t) => enabledTextSet.has(t)).length;

    const next =
      enabledSelectedCount === enabledOptions.length
        ? disabledKept
        : [...disabledKept, ...enabledOptions.map((o) => o.text)];

    setSelected(next);
    handleOnSelect(next.map((t) => textToValue.get(t) as string | number));
  }, [handleOnSelect, enabledOptions, enabledTextSet, disabledTextSet, textToValue, selected]);

  const togglePopover = useCallback(() => {
    setShowPopover((prev) => !prev);
  }, []);

  const closePopover = useCallback(() => {
    setSearchText('');
    setShowPopover(false);
  }, []);

  const handleItemClick = useCallback(
    (option: AllieSelectOptionProps) => {
      trackSelectionEvent?.(field, !!searchText.length);
      if (!multiple) {
        setShowPopover(false);
        setSearchText('');
      }
      handleOptionSelect(option);
    },
    [field, handleOptionSelect, multiple, searchText.length, trackSelectionEvent]
  );

  const handleOptionClickByIndex = useCallback(
    (index: number) => {
      const option = filteredOptions[index];
      if (option) {
        handleItemClick(option);
      }
    },
    [filteredOptions, handleItemClick]
  );

  const [prevSelectedValue, setPrevSelectedValue] = useState(selectedValue);
  if (selectedValue !== prevSelectedValue) {
    setPrevSelectedValue(selectedValue);
    setSelected(selectedValue);
  }

  const { allEnabledAreSelected, someEnabledAreSelected } = useMemo(() => {
    if (!Array.isArray(selected) || enabledOptions.length === 0) {
      return { allEnabledAreSelected: false, someEnabledAreSelected: false };
    }
    const count = selected.filter((t) => enabledTextSet.has(t)).length;
    return {
      allEnabledAreSelected: count === enabledOptions.length,
      someEnabledAreSelected: count > 0 && count < enabledOptions.length,
    };
  }, [selected, enabledOptions.length, enabledTextSet]);

  const displayText = useMemo(() => {
    if (Array.isArray(selected)) {
      if (selectAllLabel && allEnabledAreSelected) {
        return selectAllLabel;
      }
      return selected.join(', ');
    }
    return selected || '';
  }, [selected, selectAllLabel, allEnabledAreSelected]);

  const selectedCount = Array.isArray(selected) ? selected.length : 0;
  const isAllSelected = !!(selectAllLabel && allEnabledAreSelected);
  const showMultiLabel = !!(multiSelectLabel && selectedCount > 1 && !isAllSelected);

  const finalDisplayText = showMultiLabel ? `${selectedCount} ${multiSelectLabel}` : displayText;

  return (
    <div style={{ color: 'var(--allie-color-neutral-40)', ...style }} className={className}>
      <StyledSearchableItemDropdown
        disabled={disabled}
        button={true}
        lines="none"
        color={'neutral-50'}
        className="allie-text-button-normal"
        id={popoverId}
        onclick={togglePopover}
      >
        <StyledTextContainer
          weight={displayTextWeight}
          style={{ fontSize: finalDisplayText ? displayTextSize : '14px' }}
          className={!finalDisplayText ? 'select-placeholder' : ''}
        >
          {finalDisplayText || placeholder}
        </StyledTextContainer>
        {showMultiLabel && showBadgeCount && (
          <BadgeMultipleSelect>{selectedCount}</BadgeMultipleSelect>
        )}
        <IconContainer
          slot="end"
          color="neutral-50"
          icon={showPopover ? caretUpOutline : caretDownOutline}
        />
      </StyledSearchableItemDropdown>
      <IonPopover
        size="cover"
        showBackdrop={false}
        trigger={popoverId}
        isOpen={showPopover}
        onDidDismiss={closePopover}
        onDidPresent={() => {
          if (!shouldVirtualize) return;
          requestAnimationFrame(() => {
            virtualizer.measure();
            virtualizer.scrollToOffset(0);
          });
        }}
        {...popoverProps}
      >
        <div style={{ maxHeight: `${MAX_HEIGHT}px`, display: 'flex', flexDirection: 'column' }}>
          <div style={{ margin: '10px 16px 0', flexShrink: 0 }}>
            <StyledDropdownSearchBar
              value={searchText}
              onIonChange={(e: any) => setSearchText(e.detail.value!)}
              placeholder={searchPlaceholder || placeholder}
              style={{
                border: '1px solid var(--allie-color-neutral-30)',
                borderRadius: 'var(--allie-radius-md)',
                padding: '0',
              }}
            />
          </div>
          <ParentContainer ref={parentRef} maxHeight={0}>
            {multiple && selectAllLabel && (
              <StyledItem className="allie-text-body3" button onclick={handleSelectAll}>
                <IonCheckbox
                  slot="start"
                  style={{ marginRight: '20px' }}
                  checked={allEnabledAreSelected}
                  indeterminate={showSelectAllIndeterminate && someEnabledAreSelected}
                />
                {selectAllLabel}
              </StyledItem>
            )}

            {shouldVirtualize ? (
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const option = filteredOptions[virtualRow.index];
                  if (!option) return null;

                  return (
                    <div
                      key={virtualRow.key}
                      data-index={virtualRow.index}
                      style={{
                        position: 'absolute',
                        top: `${virtualRow.start}px`,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                      }}
                    >
                      <StyledItem
                        className="allie-text-body3"
                        button
                        disabled={option.disabled}
                        onclick={() => handleOptionClickByIndex(virtualRow.index)}
                      >
                        {multiple && (
                          <IonCheckbox
                            slot="start"
                            style={{ marginRight: '20px' }}
                            disabled={option.disabled}
                            checked={
                              Array.isArray(selected) ? selected.includes(option.text) : false
                            }
                          />
                        )}
                        {option.text}
                      </StyledItem>
                    </div>
                  );
                })}
              </div>
            ) : (
              <IonList>
                {filteredOptions.map((option, index) => (
                  <StyledItem
                    key={`${option.value}-${index}`}
                    className="allie-text-body3"
                    button
                    disabled={option.disabled}
                    onclick={() => handleOptionClickByIndex(index)}
                  >
                    {multiple && (
                      <IonCheckbox
                        slot="start"
                        style={{ marginRight: '20px' }}
                        disabled={option.disabled}
                        checked={Array.isArray(selected) ? selected.includes(option.text) : false}
                      />
                    )}
                    {option.text}
                  </StyledItem>
                ))}
              </IonList>
            )}
          </ParentContainer>
        </div>
      </IonPopover>
    </div>
  );
};
